import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin } from "lucide-react";
import { BRAND } from "../lib/brand";
import IndiaMap from "../components/IndiaMap";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = React.useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Name and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Thank you! Your message has been sent. Our team will be in touch.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="paper-grain">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-24 lg:py-28">
          <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
            Contact Us
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl text-wood-800 mt-4 leading-[1.03] max-w-3xl">
            Let&apos;s build something<br />worth keeping.
          </h1>
          <p className="mt-6 text-wood-700 max-w-2xl">
            Whether you have a question, a project, or a custom requirement —
            send us a quick message and we&apos;ll get back to you over email.
          </p>
        </div>
      </section>

      <section className="bg-wood-100 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="card-flat rounded-sm p-8 sm:p-10 bg-white">
              <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                Send a Message
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-wood-800 mt-2">
                Tell us about your project
              </h2>

              <form onSubmit={onSubmit} className="mt-8 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="c-name" className="text-xs uppercase tracking-widest text-wood-700">
                      Name
                    </Label>
                    <Input
                      id="c-name"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={update("name")}
                      required
                      placeholder="Your name"
                      className="bg-wood-50 border-wood-200 focus-visible:ring-gold-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-phone" className="text-xs uppercase tracking-widest text-wood-700">
                      Phone
                    </Label>
                    <Input
                      id="c-phone"
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91 9XXXXXXXXX"
                      className="bg-wood-50 border-wood-200 focus-visible:ring-gold-400"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-email" className="text-xs uppercase tracking-widest text-wood-700">
                    Email (optional)
                  </Label>
                  <Input
                    id="c-email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="bg-wood-50 border-wood-200 focus-visible:ring-gold-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-message" className="text-xs uppercase tracking-widest text-wood-700">
                    Message
                  </Label>
                  <Textarea
                    id="c-message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    placeholder="Tell us about your requirement…"
                    required
                    className="bg-wood-50 border-wood-200 focus-visible:ring-gold-400"
                  />
                </div>
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold disabled:opacity-60"
                  >
                    {loading ? "Sending…" : "Send Message"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Info + Map */}
          <div className="lg:col-span-5 space-y-6">
            <div className="card-flat rounded-sm p-8 bg-white">
              <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                Reach us
              </div>
              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-4">
                  <Mail className="text-gold-600 shrink-0 mt-1" size={18} />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-wood-700">Email</div>
                    <a
                      data-testid="contact-email-link"
                      href={`mailto:${BRAND.email}`}
                      className="text-wood-800 font-medium hover:text-gold-600 break-all"
                    >
                      {BRAND.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="text-gold-600 shrink-0 mt-1" size={18} />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-wood-700">Address</div>
                    <div data-testid="contact-address" className="text-wood-800 font-medium leading-snug">
                      {BRAND.address.line1}
                      <br />
                      {BRAND.address.line2}
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="card-flat rounded-sm p-6 bg-white">
              <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                Our Reach
              </div>
              <div className="mt-2 text-sm text-wood-700">Pan-India delivery</div>
              <div className="mt-4 aspect-[5/6] bg-wood-50 rounded-sm">
                <IndiaMap />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
